import moment from 'moment';
import t, { partial } from '../../util/translation/translation';
import { formatCurrencyNumber } from '../../util/functions';
import { getApiErrorMessage } from './series/seriesUtils';
import {
  hasMaxTwoDecimals,
  krToOre,
  parseDecimal,
} from '../transactions/connect';

// Paid events: the events service stores every amount in øre (1/100 of a
// krona). Admins type kronor in the forms; the helpers here convert both ways
// and hold the rules the service checks, so the forms can check them first.

// A paid place costs at least this much (event.PriceTooLow below it)
export const MIN_PRICE_ORE = 500;

export const WAITLIST_MODES = ['manual', 'automatic'];
export const OFFER_HOURS_DEFAULT = 12;
export const OFFER_HOURS_MAX = 336;
export const NOTIFY_EMAILS_MAX = 5;

// The VAT rates a club can charge, by its currency
const VAT_RATES = {
  SEK: [0, 6, 12, 25],
  NOK: [0, 12, 15, 25],
};

export const vatRates = (currency) =>
  VAT_RATES[`${currency || ''}`.toUpperCase()] || [0];

export const vatOption = (rate) => ({
  label: `${rate} %`,
  value: Number(rate) || 0,
});

export const vatOptions = (currency) => vatRates(currency).map(vatOption);

export const EXTERNAL_METHODS = ['cash', 'swish', 'invoice'];

const isBlank = (value) =>
  value === '' || value === null || value === undefined;

/** Øre as the kronor an admin types ('' for nothing). */
export const oreToKrInput = (ore) =>
  isBlank(ore) || Number.isNaN(Number(ore)) ? '' : `${Number(ore) / 100}`;

/** Typed kronor as øre; blank is null. */
export const krInputToOre = (value) =>
  isBlank(`${value ?? ''}`.trim()) ? null : krToOre(value);

/** A typed amount: blank, or kronor with at most two decimals. */
export const isValidKrInput = (value) =>
  isBlank(`${value ?? ''}`.trim()) || hasMaxTwoDecimals(value);

/** Typed kronor as a number (0 when blank or not a number). */
export const krInputValue = (value) => {
  const number = parseDecimal(value);
  return Number.isFinite(number) ? number : 0;
};

/** e.g. "250 SEK" (øre in, whole kronor shown without decimals). */
export const formatMoney = (ore, currency) =>
  `${formatCurrencyNumber((Number(ore) || 0) / 100)} ${currency || ''}`.trim();

export const isPaidEvent = (event) => Number(event?.price) > 0;

/** The currency an event is sold in. */
export const eventCurrency = (event, config) =>
  `${event?.currency || config?.currencyCaps || ''}`.toUpperCase();

/** What a booking of `places` places costs, in øre. */
export const bookingTotal = (places, price, bookingFee) =>
  Math.max(1, Number(places) || 1) * (Number(price) || 0) +
  (Number(bookingFee) || 0);

// ---- Event and series forms ----

/**
 * The price and waitlist fields of the event/series forms, from a saved
 * event or series (or, for a new one, the defaults - its waitlist emails
 * are the admin's own).
 */
export const paymentFormValues = (entity, { adminEmail } = {}) => {
  if (!entity) {
    return {
      price: '',
      bookingFee: '',
      vat: vatOption(0),
      waitlistMode: 'manual',
      offerHours: OFFER_HOURS_DEFAULT,
      waitlistNotifyEmails: adminEmail ? [adminEmail] : [],
    };
  }
  const paid = Number(entity.price) > 0;
  return {
    price: paid ? oreToKrInput(entity.price) : '',
    bookingFee:
      paid && Number(entity.bookingFee) > 0
        ? oreToKrInput(entity.bookingFee)
        : '',
    vat: vatOption(Number(entity.vat) || 0),
    waitlistMode: WAITLIST_MODES.includes(entity.waitlistMode)
      ? entity.waitlistMode
      : 'manual',
    offerHours: entity.offerHours || OFFER_HOURS_DEFAULT,
    waitlistNotifyEmails: Array.isArray(entity.waitlistNotifyEmails)
      ? entity.waitlistNotifyEmails
      : [],
    ...(entity.seriesPrice !== undefined && {
      seriesPrice:
        Number(entity.seriesPrice) > 0 ? oreToKrInput(entity.seriesPrice) : '',
    }),
  };
};

/**
 * The price and waitlist fields as the events service takes them. Without a
 * price the event is free: the fee and VAT are sent as 0. The currency is
 * left out (the club's).
 */
export const paymentPayload = (values) => {
  const price = krInputToOre(values.price) || 0;
  const paid = price > 0;
  return {
    price: paid ? price : null,
    bookingFee: paid ? krInputToOre(values.bookingFee) || 0 : 0,
    vat: paid ? Number(values.vat?.value ?? values.vat) || 0 : 0,
    waitlistMode: WAITLIST_MODES.includes(values.waitlistMode)
      ? values.waitlistMode
      : 'manual',
    offerHours: Number(values.offerHours) || OFFER_HOURS_DEFAULT,
    waitlistNotifyEmails: (values.waitlistNotifyEmails || []).slice(
      0,
      NOTIFY_EMAILS_MAX
    ),
  };
};

/** The whole-series price, for the series form (null when free). */
export const seriesPricePayload = (values) => {
  const price = krInputToOre(values.price) || 0;
  return {
    seriesPrice: price > 0 ? krInputToOre(values.seriesPrice) || null : null,
  };
};

export const seriesNeedsSeriesPrice = (values) =>
  (krInputToOre(values.price) || 0) > 0 &&
  ['series', 'both'].includes(values.bookingMode);

export const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(`${email || ''}`.trim());

/**
 * Checks the price and waitlist fields, adding to `errors`. `series`: also
 * the whole-series price (needed when the series is paid and can be booked
 * as a whole).
 */
export const validatePaymentValues = (values, errors, { series } = {}) => {
  const p = partial('EventPayments');
  const priceOre = krInputToOre(values.price);
  if (!isValidKrInput(values.price)) {
    errors.price = p('errorAmount');
  } else if (priceOre > 0 && priceOre < MIN_PRICE_ORE) {
    errors.price = p('errorPriceTooLow', {
      amount: formatCurrencyNumber(MIN_PRICE_ORE / 100),
    });
  }
  const paid = priceOre > 0;
  if (paid && !isValidKrInput(values.bookingFee)) {
    errors.bookingFee = p('errorAmount');
  }
  if (series && seriesNeedsSeriesPrice(values)) {
    if (isBlank(`${values.seriesPrice ?? ''}`.trim())) {
      errors.seriesPrice = t('api.series.SeriesPriceRequired');
    } else if (!isValidKrInput(values.seriesPrice)) {
      errors.seriesPrice = p('errorAmount');
    } else if (krInputToOre(values.seriesPrice) < MIN_PRICE_ORE) {
      errors.seriesPrice = p('errorPriceTooLow', {
        amount: formatCurrencyNumber(MIN_PRICE_ORE / 100),
      });
    }
  }
  if (paid && values.waitlistEnabled) {
    const hours = Number(values.offerHours);
    if (
      !Number.isInteger(hours) ||
      hours < 1 ||
      hours > OFFER_HOURS_MAX ||
      isBlank(values.offerHours)
    ) {
      errors.offerHours = p('errorOfferHours', { max: OFFER_HOURS_MAX });
    }
  }
  return errors;
};

// ---- Adding participants and invites on a paid event ----

export const PARTICIPANT_PAYMENT_MODES = ['free', 'link', 'external'];

/**
 * Start values for the forms that add someone to a paid event: the invite's
 * price (price, bookingFee) and the add-participant payment section
 * (payment*), all from the event. Empty for a free event.
 */
export const paymentInitialValues = (event) => {
  if (!isPaidEvent(event)) return {};
  const price = oreToKrInput(event.price);
  const bookingFee =
    Number(event.bookingFee) > 0 ? oreToKrInput(event.bookingFee) : '';
  return {
    price,
    bookingFee,
    paymentMode: 'free',
    paymentPrice: price,
    paymentBookingFee: bookingFee,
    paymentExpiry: '24h',
    paymentExpiresAt: '',
    externalMethod: null,
    externalAmount: '',
  };
};

/**
 * The add-participant body's payment part (amounts in øre), or undefined
 * when there's nothing to send. 'free' = a comp; 'link' = a payment link by
 * email; 'external' = paid outside the app.
 */
export const participantPaymentPayload = (values, event) => {
  if (!isPaidEvent(event) || !values?.paymentMode) return undefined;
  switch (values.paymentMode) {
    case 'link': {
      const expiresAt = expiresAtFor(
        values.paymentExpiry,
        values.paymentExpiresAt,
        event
      );
      return {
        mode: 'link',
        price: krInputToOre(values.paymentPrice) ?? Number(event.price),
        bookingFee: krInputToOre(values.paymentBookingFee) || 0,
        ...(expiresAt && { expiresAt }),
      };
    }
    case 'external':
      return {
        mode: 'external',
        externalMethod: values.externalMethod?.value || values.externalMethod,
        ...(`${values.externalAmount ?? ''}`.trim() !== '' && {
          amount: krInputToOre(values.externalAmount),
        }),
      };
    default:
      return { mode: 'free' };
  }
};

/** Errors in the add-participant payment section (validate helpers). */
export const validateParticipantPayment = (values, errors) => {
  const p = partial('EventPayments');
  if (values?.paymentMode === 'link') {
    if (!isValidKrInput(values.paymentPrice)) {
      errors.paymentPrice = p('errorAmount');
    }
    if (!isValidKrInput(values.paymentBookingFee)) {
      errors.paymentBookingFee = p('errorAmount');
    }
    if (
      values.paymentExpiry === 'custom' &&
      (!values.paymentExpiresAt ||
        !moment(values.paymentExpiresAt).isAfter(moment()))
    ) {
      errors.paymentExpiresAt = p('errorExpiryPast');
    }
  }
  if (values?.paymentMode === 'external') {
    if (!values.externalMethod) {
      errors.externalMethod = t('api.participant.ExternalMethodRequired');
    }
    if (!isValidKrInput(values.externalAmount)) {
      errors.externalAmount = p('errorAmount');
    }
  }
  return errors;
};

// ---- Deadlines for payment links and offers ----

export const EXPIRY_CHOICES = ['default', '24h', '3d', 'registrationEnd'];

/** Whether "until registration closes" can be picked for the event. */
export const canExpireAtRegistrationEnd = (event) =>
  !!event?.registrationEnd && moment(event.registrationEnd).isAfter(moment());

/**
 * The deadline for a choice: ISO, or undefined for the service's default
 * ('default': for offers, the event's offerHours).
 */
export const expiresAtFor = (choice, customValue, event) => {
  switch (choice) {
    case '24h':
      return moment().add(24, 'hours').toISOString();
    case '3d':
      return moment().add(3, 'days').toISOString();
    case 'registrationEnd':
      return canExpireAtRegistrationEnd(event)
        ? moment(event.registrationEnd).toISOString()
        : undefined;
    case 'custom':
      return customValue ? new Date(customValue).toISOString() : undefined;
    default:
      return undefined;
  }
};

// ---- Errors ----

/**
 * A payment/booking error from the events or transactions service as text.
 * EventLimitReached says how many places are left when the service tells.
 */
export const paymentErrorMessage = (
  err,
  fallbackKey = 'shared.somethingWentWrong'
) => {
  const p = partial('EventPayments');
  if (err?.data?.message === 'participant.EventLimitReached') {
    const freePlaces = err.data.freePlaces;
    if (freePlaces !== undefined && freePlaces !== null) {
      return p('eventLimitReachedFree', { count: freePlaces });
    }
  }
  return getApiErrorMessage(err, fallbackKey);
};

// ---- Places ----

/** How many places the person's group needs (them and their guests). */
export const groupSize = (person) => 1 + (Number(person?.friendAmount) || 0);

/** Whether a group fits the free places (null = no limit). */
export const groupFits = (person, freePlaces) =>
  freePlaces === null ||
  freePlaces === undefined ||
  groupSize(person) <= Number(freePlaces);

/**
 * "17 of 20 taken · 2 held" (no limit: "17 taken"); null when the event
 * payload has no counts.
 */
export const placesSummary = (counts, maxAttendees) => {
  if (
    !counts ||
    counts.placesTaken === undefined ||
    counts.placesTaken === null
  ) {
    return null;
  }
  const p = partial('EventPayments');
  const limited = counts.freePlaces !== null && counts.freePlaces !== undefined;
  // Free places are what's left after the taken and held ones
  const max =
    Number(maxAttendees) ||
    Number(counts.placesTaken) +
      (Number(counts.placesHeld) || 0) +
      Number(counts.freePlaces);
  const taken = limited
    ? p('placesTakenOf', { taken: counts.placesTaken, max })
    : p('placesTaken', { taken: counts.placesTaken });
  return Number(counts.placesHeld) > 0
    ? `${taken} · ${p('placesHeld', { count: counts.placesHeld })}`
    : taken;
};

// ---- Transactions and refunds ----

/** Øre already refunded; a legacy 'refunded' row without an amount is all. */
export const refundedAmountOf = (transaction) => {
  const price = Number(transaction?.price) || 0;
  const refunded = Number(transaction?.refundedAmount) || 0;
  if (transaction?.status === 'refunded') {
    return refunded > 0 ? Math.min(refunded, price) : price;
  }
  return Math.min(refunded, price);
};

/** Øre still refundable (only completed payments can be refunded). */
export const refundableAmount = (transaction) =>
  transaction?.status === 'completed'
    ? Math.max(
        0,
        (Number(transaction.price) || 0) - refundedAmountOf(transaction)
      )
    : 0;

/**
 * A participant's payment as shown in the list: { key, label, color, amount,
 * refundable, transaction }. key: paid | partlyRefunded | refunded |
 * external | free | pending | none.
 */
export const participantPayment = (participant, transactionsById, currency) => {
  const p = partial('EventPayments');
  const transaction = participant.transactionId
    ? transactionsById[participant.transactionId]
    : null;
  const amountText = (ore) => formatMoney(ore, currency);
  switch (participant.paymentMethod) {
    case 'stripe': {
      if (!transaction) {
        return {
          key: 'paid',
          color: 'success',
          label: p('statusPaid', {
            amount: amountText(participant.amountPaid),
          }),
          refundable: 0,
        };
      }
      const refunded = refundedAmountOf(transaction);
      const price = Number(transaction.price) || 0;
      if (transaction.status === 'refunded' || (price && refunded >= price)) {
        return {
          key: 'refunded',
          color: 'secondary',
          label: p('statusRefunded', { amount: amountText(refunded) }),
          refundable: 0,
          transaction,
        };
      }
      if (transaction.status !== 'completed') {
        return {
          key: 'pending',
          color: 'warning',
          label: t(`transactions.status-${transaction.status}`),
          refundable: 0,
          transaction,
        };
      }
      if (refunded > 0) {
        return {
          key: 'partlyRefunded',
          color: 'info',
          label: p('statusPartlyRefunded', {
            refunded: amountText(refunded),
            amount: amountText(price),
          }),
          refundable: refundableAmount(transaction),
          transaction,
        };
      }
      return {
        key: 'paid',
        color: 'success',
        label: p('statusPaid', { amount: amountText(price) }),
        refundable: refundableAmount(transaction),
        transaction,
      };
    }
    case 'external':
      return {
        key: 'external',
        color: 'info',
        label: p('statusExternal', {
          method: EXTERNAL_METHODS.includes(participant.externalPaymentMethod)
            ? p(`method-${participant.externalPaymentMethod}`)
            : '-',
          amount: amountText(participant.amountPaid),
        }),
        refundable: 0,
      };
    case 'free':
      return {
        key: 'free',
        color: 'secondary',
        label: p('statusFree'),
        refundable: 0,
      };
    default:
      return { key: 'none', color: 'light', label: '-', refundable: 0 };
  }
};
